import React, {useState} from 'react';
import {generateTestId, TestIdElementType} from '../utils/test';
import {useComponent} from '../component-library';
import {useFeatureAppId} from '../utils/use-feature-app-id';

export interface TemperatureSliderProps {
  ariaLabelledby: string;
  defaultValue: number;
  getValueText: (value: number) => string;
  tempUnit: TemperatureUnits;
  range: {
    min: number;
    max: number;
  };
  onChangeImmediate?: (value: number) => void;
  onChange: (value: number) => void;
}

export enum TemperatureUnits {
  celsius = 'temperatureCelsius',
  fahrenheit = 'temperatureFahrenheit',
}

const convertToCelsius = (
  value: number,
  inputUnit: TemperatureUnits
): number => {
  switch (inputUnit) {
    case TemperatureUnits.celsius:
      return value;
    case TemperatureUnits.fahrenheit:
      return Math.round((value - 32) / 1.8);
  }
};

const convertFromCelsius = (
  value: number,
  outputUnit: TemperatureUnits
): number => {
  switch (outputUnit) {
    case TemperatureUnits.fahrenheit:
      return Math.round(value * 1.8 + 32);
    case TemperatureUnits.celsius:
      return value;
  }
};

const convertStepFromCelsius = (
  step: number,
  unit: TemperatureUnits
): number => {
  switch (unit) {
    case TemperatureUnits.fahrenheit:
      return Math.round(step * 1.8);
    case TemperatureUnits.celsius:
      return step;
  }
};

const getBackgroundGradient = (min: number, max: number) => {
  const intervals = 1000 / (Math.abs(min) + Math.abs(max));
  const temperatureStep = 10;
  const backgroundGradientColors: Record<string, string> = {
    '-20': '#6892fa',
    '-10': '#00B0F0',
    '0': '#9dc193',
    '10': '#FFD100',
    '20': '#FF335C',
  };

  let gradient = '';
  for (let i = min, k = 0; i < max; i += temperatureStep, k++) {
    const color = backgroundGradientColors[i.toString()];
    const percentage = (k + 1) * intervals;
    const comma = i + temperatureStep < max ? ',' : '';

    gradient = gradient.concat(`${color} ${percentage}%${comma}`);
  }
  return gradient;
};

export const TemperatureSlider = React.memo(
  ({
    ariaLabelledby,
    defaultValue,
    getValueText,
    range,
    onChangeImmediate,
    onChange,
    tempUnit,
  }: TemperatureSliderProps) => {
    const Slider = useComponent('Slider');
    const featureAppId = useFeatureAppId();

    const [stateDefaultValue, setStateDefaultValue] = useState(
      convertFromCelsius(defaultValue, tempUnit)
    );

    const createUnitAwareOnChange = (
      tempUnit: TemperatureUnits,
      onChange?: (value: number) => void
    ): ((value: number) => void) | undefined => {
      if (!onChange) {
        return undefined;
      }

      return (value) => {
        const celsiusValue = convertToCelsius(value, tempUnit);
        setStateDefaultValue(value);

        onChange(celsiusValue);
      };
    };

    return (
      <Slider
        min={convertFromCelsius(range.min, tempUnit)}
        max={convertFromCelsius(range.max, tempUnit)}
        defaultValue={stateDefaultValue}
        step={convertStepFromCelsius(10, tempUnit)}
        labelId={ariaLabelledby}
        onChangeImmediate={createUnitAwareOnChange(tempUnit, onChangeImmediate)}
        onChange={createUnitAwareOnChange(tempUnit, onChange)}
        backgroundGradient={getBackgroundGradient(range.min, range.max)}
        getValueText={getValueText}
        testId={generateTestId({
          featureAppId,
          elementName: 'temperature-slider',
          elementType: TestIdElementType.dynamic,
        })}
      />
    );
  }
);

TemperatureSlider.displayName = 'TemperatureSlider';
